import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Icon, Skeleton } from '@vault/ui';
import { api } from '../../api/client.js';
import type { ContentVersion } from '../../api/client.js';
import { errMessage, formatDate } from '../_vault/shared.js';
import { formatTime, readingTime } from '../timeline/helpers.js';
import { diffWords } from './diff.js';
import type { DiffSegment } from './diff.js';

export interface VersionHistoryProps {
  itemId: string;
  isPro: boolean;
  onUpgrade: () => void;
}

/**
 * Version History (Pro) — timeline of captured snapshots for one saved page,
 * with a word-level added/removed diff between any two versions, plus
 * copy/restore of an old version's text. Renders inside the Page Detail
 * slide-over; free users see a Pro lock instead of data.
 *
 * Design source: design-handoff/…/Vault Version History.dc.html
 */
export function VersionHistory({ itemId, isPro, onUpgrade }: VersionHistoryProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const versionsQ = useQuery({
    queryKey: ['versions', itemId],
    queryFn: () => api.versions.list(itemId),
    enabled: isPro,
  });

  /** Newest first, regardless of server ordering. */
  const versions = useMemo(
    () => (versionsQ.data?.versions ?? []).slice().sort((a, b) => b.capturedAt - a.capturedAt),
    [versionsQ.data],
  );

  // Reset selection when the panel is reused for another item.
  useEffect(() => {
    setSelected([]);
    setCopied(false);
  }, [itemId]);

  // Default selection: current + the version just before it.
  useEffect(() => {
    if (selected.length > 0 || versions.length === 0) return;
    setSelected(versions.slice(0, 2).map((v) => v.id));
  }, [versions, selected.length]);

  const selVersions = useMemo(
    () =>
      selected
        .map((id) => versions.find((v) => v.id === id))
        .filter((v): v is ContentVersion => v !== undefined)
        .sort((a, b) => b.capturedAt - a.capturedAt),
    [selected, versions],
  );
  const comparing = selVersions.length === 2;
  const newer = comparing ? selVersions[0] : undefined;
  /** The "focused" version — diff base and target of copy/restore actions. */
  const older = comparing ? selVersions[1] : selVersions[0];

  const newerQ = useQuery({
    queryKey: ['version-content', itemId, newer?.id],
    queryFn: () => api.versions.get(itemId, newer!.id),
    enabled: isPro && newer !== undefined,
    staleTime: Infinity, // an immutable snapshot never changes
  });
  const olderQ = useQuery({
    queryKey: ['version-content', itemId, older?.id],
    queryFn: () => api.versions.get(itemId, older!.id),
    enabled: isPro && older !== undefined,
    staleTime: Infinity,
  });

  const diff = useMemo(() => {
    if (!comparing || !newerQ.data || !olderQ.data) return null;
    return diffWords(textOf(olderQ.data.content), textOf(newerQ.data.content));
  }, [comparing, newerQ.data, olderQ.data]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  function toggle(id: string): void {
    setSelected((cur) => {
      if (cur.includes(id)) {
        // keep at least one selected
        return cur.length === 2 ? cur.filter((x) => x !== id) : cur;
      }
      if (cur.length < 2) return [...cur, id];
      // both slots taken → drop the earliest pick, keep the latest + new
      return [cur[1] as string, id];
    });
  }

  function copyVersion(): void {
    const content = olderQ.data?.content;
    if (!content) return;
    void navigator.clipboard?.writeText(textOf(content));
    setCopied(true);
  }

  /** "Restore" without a write endpoint: save the snapshot text as a file. */
  function restoreVersion(): void {
    const content = olderQ.data?.content;
    if (!content || !older) return;
    const blob = new Blob([textOf(content)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tabvault-version-${new Date(older.capturedAt).toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Pro lock ──────────────────────────────────────────────────────────────
  if (!isPro) return <ProLock onUpgrade={onUpgrade} />;

  const changes = Math.max(0, versions.length - 1);
  const contentPending =
    (older !== undefined && olderQ.isPending) || (newer !== undefined && newerQ.isPending);
  const contentError = olderQ.isError || newerQ.isError;

  return (
    <div className="vhx-root">
      <style>{VH_CSS}</style>

      {/* versions rail */}
      <div className="vhx-rail">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' }}>
            VERSIONS
          </span>
          <Badge tone="pro" pill={false}>
            PRO
          </Badge>
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>
            KEPT FOREVER
          </span>
        </div>

        <div className="vhx-list">
          {versionsQ.isPending &&
            [0, 1, 2].map((i) => <Skeleton key={i} height={58} radius={12} />)}

          {versionsQ.isError && (
            <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-3)' }}>
              Couldn't load versions.
            </span>
          )}

          {versions.map((v, i) => (
            <VersionRow
              key={v.id}
              version={v}
              prev={versions[i + 1]}
              selected={selected.includes(v.id)}
              label={rowLabel(v, versions[i + 1], selected, comparing, newer?.id)}
              onClick={() => toggle(v.id)}
            />
          ))}
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
          <Button
            variant="ghost"
            fullWidth
            disabled={!olderQ.data}
            onClick={copyVersion}
            icon={copied ? <Icon name="check" size={13} color="var(--success)" /> : <CopyGlyph />}
          >
            {copied ? 'Copied to clipboard' : "Copy this version's text"}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            disabled={!olderQ.data}
            onClick={restoreVersion}
            icon={<Icon name="download" size={13} color="currentColor" />}
          >
            Restore this version
          </Button>
          {changes > 0 && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)', textAlign: 'center' }}>
              THE PAGE CHANGED {changes} TIME{changes === 1 ? '' : 'S'} SINCE YOU FIRST SAVED IT
            </span>
          )}
        </div>
      </div>

      {/* diff reader */}
      <div className="vhx-diff">
        <div className="vhx-diff-head">
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' }}>
            {comparing ? 'COMPARING' : 'VIEWING'}
          </span>
          {older && (
            <span className="vhx-pill">
              {comparing && newer ? `${formatDate(newer.capturedAt)} ↔ ${formatDate(older.capturedAt)}` : formatDate(older.capturedAt)}
            </span>
          )}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>
            {comparing ? (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <i className="vhx-sw vhx-sw-add" />
                  ADDED
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <i className="vhx-sw vhx-sw-rem" />
                  REMOVED
                </span>
              </>
            ) : (
              versions.length > 1 && <span>PICK A SECOND VERSION TO COMPARE</span>
            )}
          </span>
        </div>

        <div className="vhx-diff-body">
          {versionsQ.isPending && <ArticleSkeleton />}

          {!versionsQ.isPending && versionsQ.isError && (
            <CenterState>
              <EmptyState
                dashed={false}
                icon={<Icon name="clock" size={18} color="var(--text-3)" />}
                title="Couldn't load version history"
                description={errMessage(versionsQ.error)}
                action={
                  <Button variant="secondary" size="sm" onClick={() => void versionsQ.refetch()}>
                    Try again
                  </Button>
                }
              />
            </CenterState>
          )}

          {!versionsQ.isPending && !versionsQ.isError && versions.length <= 1 && (
            <CenterState>
              <EmptyState
                dashed={false}
                icon={<Icon name="clock" size={18} color="var(--text-3)" />}
                title="Only the current version so far"
                description="History builds as this page changes — every time Nomad Tabs re-captures it, the previous text is kept here."
              />
            </CenterState>
          )}

          {!versionsQ.isPending && !versionsQ.isError && versions.length > 1 && (
            <>
              {contentPending && <ArticleSkeleton />}

              {!contentPending && contentError && (
                <CenterState>
                  <EmptyState
                    dashed={false}
                    icon={<Icon name="clock" size={18} color="var(--text-3)" />}
                    title="Couldn't load these snapshots"
                    description={errMessage(olderQ.error ?? newerQ.error)}
                    action={
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          if (olderQ.isError) void olderQ.refetch();
                          if (newerQ.isError) void newerQ.refetch();
                        }}
                      >
                        Try again
                      </Button>
                    }
                  />
                </CenterState>
              )}

              {!contentPending && !contentError && diff && newer && (
                <DiffArticle diff={diff} newerDate={formatDate(newer.capturedAt)} />
              )}

              {!contentPending && !contentError && !comparing && older && olderQ.data && (
                <SingleArticle content={olderQ.data.content} version={older} />
              )}
            </>
          )}
        </div>
        <div className="vhx-fade" />
      </div>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

function textOf(content: { text: string; excerpt: string }): string {
  return content.text || content.excerpt || '';
}

/** "Mar 8, 22:10" — the row stamp from the design. */
function stamp(ts: number): string {
  return `${new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${formatTime(ts)}`;
}

interface RowLabel {
  text: string;
  color: string;
}

function rowLabel(
  v: ContentVersion,
  prev: ContentVersion | undefined,
  selected: string[],
  comparing: boolean,
  newerId: string | undefined,
): RowLabel {
  if (selected.includes(v.id)) {
    if (comparing) return { text: v.id === newerId ? 'NEWER' : 'OLDER', color: 'var(--accent-ink)' };
    return { text: 'VIEWING', color: 'var(--accent-ink)' };
  }
  if (!prev) return { text: 'ORIGINAL', color: 'var(--text-3)' };
  const delta = v.wordCount - prev.wordCount;
  if (delta > 0) return { text: `+${delta.toLocaleString()} WORDS`, color: 'var(--success)' };
  if (delta < 0) return { text: `−${Math.abs(delta).toLocaleString()} WORDS`, color: 'var(--danger)' };
  return { text: 'EDITED', color: 'var(--text-3)' };
}

// ── sub-views ───────────────────────────────────────────────────────────────

interface VersionRowProps {
  version: ContentVersion;
  prev: ContentVersion | undefined;
  selected: boolean;
  label: RowLabel;
  onClick: () => void;
}

function VersionRow({ version, prev, selected, label, onClick }: VersionRowProps) {
  const meta = version.current
    ? `CURRENT · ${readingTime(version.wordCount)} MIN READ`
    : prev
      ? `${version.wordCount.toLocaleString()} WORDS`
      : `FIRST SAVED · ${version.wordCount.toLocaleString()} WORDS`;
  return (
    <button
      type="button"
      className="vhx-row"
      data-selected={selected || undefined}
      onClick={onClick}
      title={version.excerpt}
      aria-pressed={selected ? 'true' : 'false'}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: selected ? 'var(--accent)' : 'var(--surface-3)',
          flexShrink: 0,
        }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{stamp(version.capturedAt)}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
          {meta}
        </span>
      </span>
      <span
        style={{
          marginLeft: 'auto',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: label.color,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {label.text}
      </span>
    </button>
  );
}

function DiffArticle({ diff, newerDate }: { diff: ReturnType<typeof diffWords>; newerDate: string }) {
  return (
    <div className="vhx-article">
      {diff.added === 0 && diff.removed === 0 && (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--text-3)' }}>
          NO TEXT CHANGES BETWEEN THESE VERSIONS
        </span>
      )}
      {diff.paragraphs.length === 0 && (
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--text-3)' }}>
          Neither version captured any text.
        </p>
      )}
      {diff.paragraphs.map((para, i) => (
        <DiffParagraph key={i} segments={para} newerDate={newerDate} />
      ))}
    </div>
  );
}

function DiffParagraph({ segments, newerDate }: { segments: DiffSegment[]; newerDate: string }) {
  const allAdded = segments.every((s) => s.op === 'added');
  const allRemoved = segments.every((s) => s.op === 'removed');
  const joined = segments.map((s) => s.text).join(' ');

  if (allAdded) {
    return (
      <p className="vhx-p vhx-p-added">
        {joined} <span className="vhx-mark" style={{ color: 'var(--success)' }}>ADDED {newerDate}</span>
      </p>
    );
  }
  if (allRemoved) {
    return (
      <p className="vhx-p vhx-p-removed">
        {joined} <span className="vhx-mark vhx-mark-rem">REMOVED</span>
      </p>
    );
  }
  return (
    <p className="vhx-p">
      {segments.map((s, j) => (
        <Fragment key={j}>
          {j > 0 && ' '}
          {s.op === 'same' ? s.text : <span className={s.op === 'added' ? 'vhx-w-added' : 'vhx-w-removed'}>{s.text}</span>}
        </Fragment>
      ))}
    </p>
  );
}

function SingleArticle({
  content,
  version,
}: {
  content: { text: string; excerpt: string; wordCount: number };
  version: ContentVersion;
}) {
  const paragraphs = textOf(content)
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <div className="vhx-article">
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--amber-ink)' }}>
        {version.current ? 'CURRENT VERSION' : 'FULL TEXT'} · SAVED {formatDate(version.capturedAt)} ·{' '}
        {content.wordCount.toLocaleString()} WORDS
      </span>
      {paragraphs.length === 0 && (
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--text-3)' }}>
          This version captured no text.
        </p>
      )}
      {paragraphs.map((p, i) => (
        <p key={i} className="vhx-p">
          {p}
        </p>
      ))}
    </div>
  );
}

function ArticleSkeleton() {
  return (
    <div className="vhx-article">
      <Skeleton width={200} height={10} />
      {[94, 88, 96, 72, 90, 60].map((w, i) => (
        <Skeleton key={i} width={`${w}%`} height={13} />
      ))}
    </div>
  );
}

function CenterState({ children }: { children: ReactNode }) {
  return (
    <div style={{ width: 'min(640px,90%)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {children}
    </div>
  );
}

function ProLock({ onUpgrade }: { onUpgrade: () => void }) {
  const bullets = [
    'Every re-save keeps a full text snapshot — forever',
    'Compare any two versions with an added / removed diff',
    'Restore or copy the text of any older version',
  ];
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        fontFamily: 'var(--font-body)',
        color: 'var(--text-1)',
      }}
    >
      <div
        style={{
          width: 'min(420px, 100%)',
          background: 'var(--surface-1)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '28px 26px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 13,
            background: 'var(--surface-2)',
            border: '1px solid var(--line-strong)',
          }}
        >
          <Icon name="lock" size={17} color="var(--amber-ink)" />
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.12em', color: 'var(--amber-ink)' }}>
          VERSION HISTORY <Badge tone="pro" pill={false}>PRO</Badge>
        </span>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, letterSpacing: '-.01em' }}>
          Version history is a Pro feature
        </span>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left' }}>
          {bullets.map((b) => (
            <li key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)' }}>
              <Icon name="check" size={12} color="var(--success)" style={{ flexShrink: 0, marginTop: 3 }} />
              {b}
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 4 }}>
          <Button variant="primary" onClick={onUpgrade}>
            Upgrade to Pro
          </Button>
        </div>
      </div>
    </div>
  );
}

function CopyGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <rect x="1.8" y="4.8" width="9.4" height="9.4" rx="2.4" />
      <path d="M5 4.4V4a2.2 2.2 0 0 1 2.2-2.2H12A2.2 2.2 0 0 1 14.2 4v4.8A2.2 2.2 0 0 1 12 11h-.4" />
    </svg>
  );
}

// ── styles (design-handoff parity, tokens only) ────────────────────────────

const VH_CSS = `
.vhx-root{height:100%;display:flex;min-height:0;font-family:var(--font-body);color:var(--text-1)}
.vhx-rail{width:clamp(240px,32%,320px);flex-shrink:0;border-right:1px solid var(--line);background:var(--surface-0);display:flex;flex-direction:column;gap:14px;padding:20px 22px;overflow:auto}
.vhx-list{display:flex;flex-direction:column;gap:8px}
.vhx-row{display:flex;align-items:center;gap:11px;background:var(--surface-1);border:1px solid var(--line);border-radius:12px;padding:12px 14px;cursor:pointer;width:100%;text-align:left;font-family:inherit;color:inherit;appearance:none}
.vhx-row:hover{border-color:var(--line-strong)}
.vhx-row:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.vhx-row[data-selected]{background:var(--accent-tint);border-color:var(--line-accent)}
.vhx-row[data-selected]:hover{border-color:var(--line-accent)}
.vhx-diff{flex:1;min-width:0;position:relative;display:flex;flex-direction:column;min-height:0}
.vhx-diff-head{display:flex;align-items:center;gap:10px;padding:14px 32px;border-bottom:1px solid var(--line);flex-shrink:0;flex-wrap:wrap}
.vhx-pill{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line-accent);background:var(--accent-tint);border-radius:999px;padding:4px 11px;font-family:var(--font-mono);font-size:9.5px;color:var(--accent-ink);white-space:nowrap}
.vhx-sw{width:8px;height:8px;border-radius:2px;display:inline-block}
.vhx-sw-add{background:var(--success-tint);border:1px solid var(--success-line)}
.vhx-sw-rem{background:var(--danger-tint);border:1px solid var(--danger-line)}
.vhx-diff-body{flex:1;overflow:auto;display:flex;justify-content:center;padding:36px 0}
.vhx-article{width:min(640px,90%);display:flex;flex-direction:column;gap:18px}
.vhx-p{margin:0;font-size:14.5px;line-height:1.75;color:var(--text-2)}
.vhx-p-added{background:var(--success-tint);border-left:3px solid var(--success);border-radius:6px;padding:10px 14px}
.vhx-p-removed{color:var(--text-3);background:var(--danger-tint);border-left:3px solid var(--danger);border-radius:6px;padding:10px 14px;text-decoration:line-through}
.vhx-mark{font-family:var(--font-mono);font-size:9px;letter-spacing:.08em}
.vhx-mark-rem{color:var(--danger);text-decoration:none;display:inline-block}
.vhx-w-added{background:var(--success-tint);color:var(--text-1);border-radius:4px;padding:1px 3px}
.vhx-w-removed{background:var(--danger-tint);color:var(--text-3);border-radius:4px;padding:1px 3px;text-decoration:line-through}
.vhx-fade{position:absolute;left:0;right:0;bottom:0;height:56px;background:linear-gradient(transparent,var(--bg));pointer-events:none}
@media (max-width:860px){
  .vhx-root{flex-direction:column;overflow:auto;height:auto}
  .vhx-rail{width:auto;border-right:none;border-bottom:1px solid var(--line);overflow:visible}
  .vhx-diff{overflow:visible;min-height:auto}
  .vhx-diff-body{overflow:visible;padding:24px 0}
  .vhx-fade{display:none}
}
`;
